/**
 * 滤镜应用（M3-13 / DESIGN.md §5.4）。
 *
 * 对活动 raster 图层的**内容范围**（已分配块的并集，不是整个画布）执行：
 * 展平 → wasm 滤镜 → 写回受影响块，一次调用 = 一段像素事务 = 一条历史，
 * 与画笔/填充同一纪律（M1-08）。
 */
import {
  gaussianBlur,
  unsharpMask,
  motionBlur,
  surfaceBlur,
  addNoise,
  emboss,
  findEdges,
  type UnsharpMaskParams,
  type MotionBlurParams,
  type SurfaceBlurParams,
  type AddNoiseParams,
  type EmbossParams,
} from '../engine/filters';
import { TILE, tileKey, type LayerId } from '../engine/tile/key';
import { BYTES_PER_TILE } from '../engine/tile/store';
import type { AppStore } from './store';

/** 活动 raster 图层已分配块的文档空间外接矩形。无内容返回 null。 */
function contentBounds(
  store: AppStore,
  layer: LayerId,
): { x: number; y: number; width: number; height: number } | null {
  let minTx = Infinity;
  let minTy = Infinity;
  let maxTx = -Infinity;
  let maxTy = -Infinity;
  for (const tile of store.tiles.residentTilesOf(layer, 0)) {
    minTx = Math.min(minTx, tile.key.tx);
    minTy = Math.min(minTy, tile.key.ty);
    maxTx = Math.max(maxTx, tile.key.tx);
    maxTy = Math.max(maxTy, tile.key.ty);
  }
  if (!Number.isFinite(minTx)) return null;
  return {
    x: minTx * TILE,
    y: minTy * TILE,
    width: (maxTx - minTx + 1) * TILE,
    height: (maxTy - minTy + 1) * TILE,
  };
}

/** 展平一个矩形区域到 RGBA8 缓冲（仅本图层，不合成其他图层）。 */
function flatten(
  store: AppStore,
  layer: LayerId,
  bounds: { x: number; y: number; width: number; height: number },
): Uint8Array {
  const out = new Uint8Array(bounds.width * bounds.height * 4);
  for (const tile of store.tiles.residentTilesOf(layer, 0)) {
    const data = tile.data;
    if (data === null) continue;
    const originX = tile.key.tx * TILE;
    const originY = tile.key.ty * TILE;
    for (let y = 0; y < TILE; y++) {
      const docY = originY + y;
      const dy = docY - bounds.y;
      if (dy < 0 || dy >= bounds.height) continue;
      for (let x = 0; x < TILE; x++) {
        const docX = originX + x;
        const dx = docX - bounds.x;
        if (dx < 0 || dx >= bounds.width) continue;
        const from = (y * TILE + x) * 4;
        const to = (dy * bounds.width + dx) * 4;
        out[to] = data[from]!;
        out[to + 1] = data[from + 1]!;
        out[to + 2] = data[from + 2]!;
        out[to + 3] = data[from + 3]!;
      }
    }
  }
  return out;
}

/** 把展平结果写回 tiles，一段像素事务；返回受影响块坐标。 */
function writeBack(
  store: AppStore,
  layer: LayerId,
  bounds: { x: number; y: number; width: number; height: number },
  pixels: Uint8Array,
  label: string,
): Array<{ tx: number; ty: number }> {
  const tx0 = Math.floor(bounds.x / TILE);
  const ty0 = Math.floor(bounds.y / TILE);
  const tx1 = Math.floor((bounds.x + bounds.width - 1) / TILE);
  const ty1 = Math.floor((bounds.y + bounds.height - 1) / TILE);

  const transaction = store.beginPixelTransaction(label);
  const keys = [];
  for (let ty = ty0; ty <= ty1; ty++) {
    for (let tx = tx0; tx <= tx1; tx++) {
      const key = tileKey(layer, tx, ty);
      transaction.touch(key);
      keys.push({ tx, ty, key });
    }
  }
  transaction.capture();

  const touched: Array<{ tx: number; ty: number }> = [];
  for (const { tx, ty, key } of keys) {
    const out = new Uint8Array(BYTES_PER_TILE);
    const originX = tx * TILE;
    const originY = ty * TILE;
    let any = false;
    for (let y = 0; y < TILE; y++) {
      const docY = originY + y;
      const sy = docY - bounds.y;
      if (sy < 0 || sy >= bounds.height) continue;
      for (let x = 0; x < TILE; x++) {
        const docX = originX + x;
        const sx = docX - bounds.x;
        if (sx < 0 || sx >= bounds.width) continue;
        const from = (sy * bounds.width + sx) * 4;
        if (pixels[from + 3] === 0) continue;
        const to = (y * TILE + x) * 4;
        out[to] = pixels[from]!;
        out[to + 1] = pixels[from + 1]!;
        out[to + 2] = pixels[from + 2]!;
        out[to + 3] = pixels[from + 3]!;
        any = true;
      }
    }
    if (any) {
      store.tiles.set(key, out);
      touched.push({ tx, ty });
    }
  }

  store.commitPixels(label, transaction.end());
  store.invalidatePixels(touched);
  return touched;
}

export interface FilterCallOptions {
  readonly signal?: AbortSignal;
}

/** 对活动图层执行高斯模糊。返回是否有内容被处理。 */
export async function applyGaussianBlur(
  store: AppStore,
  radius: number,
  options: FilterCallOptions = {},
): Promise<boolean> {
  const layer = store.activeRasterLayerId();
  if (layer === null) return false;
  const bounds = contentBounds(store, layer);
  if (bounds === null) return false;

  const source = flatten(store, layer, bounds);
  const blurred = await gaussianBlur(source, bounds.width, bounds.height, radius, options);
  writeBack(store, layer, bounds, blurred, '高斯模糊');
  return true;
}

/** 对活动图层执行 USM 锐化。返回是否有内容被处理。 */
export async function applyUnsharpMask(
  store: AppStore,
  params: UnsharpMaskParams,
  options: FilterCallOptions = {},
): Promise<boolean> {
  const layer = store.activeRasterLayerId();
  if (layer === null) return false;
  const bounds = contentBounds(store, layer);
  if (bounds === null) return false;

  const source = flatten(store, layer, bounds);
  const sharpened = await unsharpMask(source, bounds.width, bounds.height, params, options);
  writeBack(store, layer, bounds, sharpened, 'USM 锐化');
  return true;
}

/** 对活动图层执行动感模糊。返回是否有内容被处理。 */
export async function applyMotionBlur(
  store: AppStore,
  params: MotionBlurParams,
  options: FilterCallOptions = {},
): Promise<boolean> {
  const layer = store.activeRasterLayerId();
  if (layer === null) return false;
  const bounds = contentBounds(store, layer);
  if (bounds === null) return false;

  const source = flatten(store, layer, bounds);
  const blurred = await motionBlur(source, bounds.width, bounds.height, params, options);
  writeBack(store, layer, bounds, blurred, '动感模糊');
  return true;
}

/** 对活动图层执行表面模糊。返回是否有内容被处理。 */
export async function applySurfaceBlur(
  store: AppStore,
  params: SurfaceBlurParams,
  options: FilterCallOptions = {},
): Promise<boolean> {
  const layer = store.activeRasterLayerId();
  if (layer === null) return false;
  const bounds = contentBounds(store, layer);
  if (bounds === null) return false;

  const source = flatten(store, layer, bounds);
  const blurred = await surfaceBlur(source, bounds.width, bounds.height, params, options);
  writeBack(store, layer, bounds, blurred, '表面模糊');
  return true;
}

/** 对活动图层执行添加杂色。返回是否有内容被处理。 */
export async function applyAddNoise(
  store: AppStore,
  params: AddNoiseParams,
  options: FilterCallOptions = {},
): Promise<boolean> {
  const layer = store.activeRasterLayerId();
  if (layer === null) return false;
  const bounds = contentBounds(store, layer);
  if (bounds === null) return false;

  const source = flatten(store, layer, bounds);
  const noisy = await addNoise(source, bounds.width, bounds.height, params, options);
  writeBack(store, layer, bounds, noisy, '添加杂色');
  return true;
}

/** 对活动图层执行浮雕。返回是否有内容被处理。 */
export async function applyEmboss(
  store: AppStore,
  params: EmbossParams,
  options: FilterCallOptions = {},
): Promise<boolean> {
  const layer = store.activeRasterLayerId();
  if (layer === null) return false;
  const bounds = contentBounds(store, layer);
  if (bounds === null) return false;

  const source = flatten(store, layer, bounds);
  const embossed = await emboss(source, bounds.width, bounds.height, params, options);
  writeBack(store, layer, bounds, embossed, '浮雕');
  return true;
}

/** 对活动图层执行查找边缘（没有参数）。返回是否有内容被处理。 */
export async function applyFindEdges(
  store: AppStore,
  options: FilterCallOptions = {},
): Promise<boolean> {
  const layer = store.activeRasterLayerId();
  if (layer === null) return false;
  const bounds = contentBounds(store, layer);
  if (bounds === null) return false;

  const source = flatten(store, layer, bounds);
  const edges = await findEdges(source, bounds.width, bounds.height, options);
  writeBack(store, layer, bounds, edges, '查找边缘');
  return true;
}
